<template>
  <div 
    class="th-btn"
    :class="[
      type ? `th-${type}-btn` : '',
      disabled || loading ? type === 'link' ? 'btn-dis-link' : type === 'item-active' ? 'btn-dis-active' : 'btn-dis' : ''
    ]"
    @click="handleClick"
  >
    <!-- loading 图标 -->
    <span v-if="loading" class="th-loading">
      <svg class="loading-icon" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
        <path d="M512 64q14.016 0 23.008 8.992T544 96v192q0 14.016-8.992 23.008T512 320t-23.008-8.992T480 288V96q0-14.016 8.992-23.008T512 64zm0 640q14.016 0 23.008 8.992T544 736v192q0 14.016-8.992 23.008T512 960t-23.008-8.992T480 928V736q0-14.016 8.992-23.008T512 704zm448-192q0 14.016-8.992 23.008T928 544H736q-14.016 0-23.008-8.992T704 512t8.992-23.008T736 480h192q14.016 0 23.008 8.992T960 512zm-640 0q0 14.016-8.992 23.008T288 544H96q-14.016 0-23.008-8.992T64 512t8.992-23.008T96 480h192q14.016 0 23.008 8.992T320 512zM195.008 195.008q10.016-8.992 23.008-8.992t22.016 8.992l136 136q8.992 10.016 8.992 23.008t-8.992 22.016-23.008 8.992-22.016-8.992l-136-136q-8.992-10.016-8.992-23.008t8.992-22.016zm452 452q10.016-8.992 23.008-8.992t22.016 8.992l136 136q8.992 10.016 8.992 23.008t-8.992 22.016-23.008 8.992-22.016-8.992l-136-136q-8.992-10.016-8.992-23.008t8.992-22.016zM828.992 195.008q8.992 10.016 8.992 23.008t-8.992 22.016l-136 136q-10.016 8.992-23.008 8.992t-22.016-8.992-8.992-23.008 8.992-22.016l136-136q10.016-8.992 23.008-8.992t22.016 8.992zm-452 452q8.992 10.016 8.992 23.008t-8.992 22.016l-136 136q-10.016 8.992-23.008 8.992t-22.016-8.992-8.992-23.008 8.992-22.016l136-136q10.016-8.992 23.008-8.992t22.016 8.992z"/>
      </svg>
    </span>
    
    <!-- 前缀图标插槽 -->
    <slot v-if="!loading" name="prefix"></slot>
    
    <!-- 默认内容插槽 -->
    <div class="btn-content">
      <slot v-if="!loading">{{ text }}</slot>
      <span v-else>{{ loadingText || text }}</span>
    </div>
    
    <!-- 后缀图标插槽 -->
    <slot v-if="!loading" name="suffix"></slot>
  </div>
</template>

<script setup>
import { defineProps, defineEmits } from 'vue'

const props = defineProps({
  // 按钮类型: default(默认边框按钮)、primary(主要按钮)、linear(渐变按钮)、link(链接按钮)、text(文本按钮)、del(删除按钮) success(成功按钮)
  type: {
    type: String,
    default: 'default'
  },
  // 按钮文本
  text: {
    type: String,
    default: ''
  },
  // 是否禁用
  disabled: {
    type: Boolean,
    default: false
  },
  // 是否加载中
  loading: {
    type: Boolean,
    default: false
  },
  // 加载中的文本
  loadingText: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['click'])

const handleClick = (e) => {
  if (props.disabled || props.loading) return
  emit('click', e)
}
</script>

<style scoped>
.th-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  padding: 0 20px;
  height: 41px;
  opacity: 1;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0em;
  cursor: pointer;
  box-sizing: border-box;
  gap: 4px;
}

.th-btn:hover {
  opacity: 0.8;
}

.th-default-btn {
  background: #FFFFFF;
  box-sizing: border-box;
  border: 1px solid var(--theme-color);
  color: var(--theme-color);
}

.th-tab-btn {
  word-break: keep-all;
  /* line-height: 32px; */
  height: 32px;
  font-size: 12px;
  font-family: PingFang SC-Regular, PingFang SC;
  font-weight: 400;
  padding: 0 16px;
  cursor: pointer;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 0 !important;
  background: #f9fafb;
  box-sizing: border-box;
  border: 1px solid #e5e7eb;
  color: #4b5563;
}

.th-tab-active-btn {
  word-break: keep-all;
  /* line-height: 32px; */
  height: 32px;
  font-size: 12px;
  font-family: PingFang SC-Regular, PingFang SC;
  font-weight: 400;
  padding: 0 16px;
  cursor: pointer;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 0 !important;
  background: #eff6ff;
  box-sizing: border-box;
  border: 1px solid var(--theme-color);
  color: var(--theme-color);
}

.th-primary-btn {
  background: var(--theme-color);
  color: #FFFFFF;
}

.th-warning-btn {
  background: #f97316;
  color: #FFFFFF;
}

.th-success-btn {
  background: #00B578;
  color: #FFFFFF;
}

.th-linear-btn {
  background: linear-gradient(106deg, #1E88E5 0%, #00B578 99%);
  color: #FFFFFF;
}

.th-link-btn {
  color: var(--theme-color);
}

.th-link-btn .th-loading svg path {
  fill: #c0c6cc !important;
}

.th-text-btn {
  color: var(--text-color-assist);
}

.th-item-btn {
  word-break: keep-all;
  /* line-height: 32px; */
  height: 32px;
  font-size: 12px;
  font-family: PingFang SC-Regular, PingFang SC;
  font-weight: 400;
  color: #333333;
  background: #FAFAFA;
  border: 1px solid #c0c6cc;
  padding: 0 16px;
  cursor: pointer;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 0 !important;
}

.th-item-btn:hover {
  border: 1px solid #737373 !important;
}

.th-item-active-btn {
  word-break: keep-all;
  /* line-height: 32px; */
  height: 32px;
  font-size: 12px;
  font-family: PingFang SC-Regular, PingFang SC;
  font-weight: 400;
  color: #FFFFFF;
  background: #6196FF;
  border: 1px solid #c0c6cc;
  padding: 0 16px;
  cursor: pointer;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 0 !important;
}

.th-item-active-btn:hover {
  color: #FFF;
  background: #4f89fd;
  border: 1px solid #4f89fd;
}

.btn-dis-active {
  background: #bbb !important;
  cursor: no-drop !important;
}

.btn-dis-link {
  color: #c0c6cc !important;
  cursor: not-allowed !important;
}

.btn-dis-active:hover {
  border-color: #bbb !important;
}

.btn-dis {
  background: #bbb !important;
  cursor: not-allowed !important;
  opacity: 0.6;
}

/* Loading 相关样式 */
.th-loading {
  display: flex;
  align-items: center;
}

.loading-icon {
  width: 16px;
  height: 16px;
  animation: loading-rotate 1s linear infinite;
}

.btn-content {
  display: flex;
  align-items: center;
  line-height: 1;
}

.th-default-btn .loading-icon {
  fill: var(--theme-color);
}

.th-primary-btn .loading-icon,
.th-linear-btn .loading-icon {
  fill: #FFFFFF;
}

.th-link-btn .loading-icon {
  fill: var(--theme-color);
}

.th-text-btn .loading-icon {
  fill: var(--text-color-assist);
}

@keyframes loading-rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* 添加删除按钮样式 */
.th-del-btn {
  background: #EA0000;
  color: #FFFFFF;
}

.th-del-btn .loading-icon {
  fill: #FFFFFF;
}
</style> 